export const coreFlowsMarkdown = `# Core Integration Flows

This page describes common partner integration patterns.

## Flow 1: Employee sync

Goal: keep ERP employee master data aligned with CimmplePay.

Typical pattern:

1. Pull employees by company.
2. Upsert into ERP using external id mapping.
3. Store mapping \`{cimmplepayEmployeeId <-> erpEmployeeId}\`.
4. Repeat on schedule (for example, every 15-60 minutes).

Primary endpoint:

- \`GET /api/employees?companyId={companyId}\`

## Flow 2: Payroll run lifecycle

Goal: create and process payroll from ERP actions.

Typical sequence:

1. Create payroll run.
2. Update hours/inputs if needed.
3. Process payroll.
4. Recalculate (optional).
5. Approve and finalize.

Primary endpoints:

- \`POST /api/payroll\`
- \`PUT /api/payroll/{id}\`
- \`POST /api/payroll/{id}/process\`
- \`POST /api/payroll/{id}/recalculate\`
- \`POST /api/payroll/{id}/approve\`
- \`POST /api/payroll/{id}/finalize\`

## Flow 3: Payroll outputs to ERP

Goal: consume completed payroll results for reporting and accounting.

Typical pattern:

1. Read finalized payroll runs.
2. Retrieve run-level accounting breakdown.
3. Retrieve employee-level pay/tax details where needed.

Primary endpoints:

- \`GET /api/payroll?companyId={companyId}\`
- \`GET /api/payroll/{id}/accounting-breakdown\`
- \`GET /api/payroll/{id}/employee/{employeeId}/breakdown\`
- \`GET /api/employees/{id}/pay-history\`
- \`GET /api/employees/{id}/tax-history\`

## Flow 4: Cimmple ERP journal sync (optional)

If using Cimmple ERP journal posting:

1. Finalize payroll run.
2. Preview journal.
3. Sync journal to ERP.
4. Store returned ERP journal id and sync status.

Primary endpoints:

- \`GET /api/payroll/{id}/erp-journal-preview\`
- \`POST /api/payroll/{id}/erp-journal/sync\`

For field-level setup and account mapping details, see internal implementation note:

- \`MD files/ERP_ACCOUNTING_INTEGRATION.md\`

## Flow 5: Retry and idempotency basics

- Do not repeat write calls blindly after timeouts.
- Use your own external reference ids in logs and mapping tables.
- Retry safe reads aggressively; retry writes conservatively with status checks.
- After failed writes, verify run state with \`GET\` before replaying.
`;
